import { useEffect, useRef, type RefObject } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "../../../../components/Checkbox";
import type { DefaultTagDefinition } from "../../../../types/tags";
import {
  configurableColumns,
  resolveColumnLayout,
  type PlayersColumn,
  type PlayersColumnLayout,
} from "./playersTableColumns";
import { tourAnchor } from "../../../tours/tourAnchors";
import "./ColumnSettingsPanel.css";

/**
 * Show/hide and reorder the players table's configurable columns.
 *
 * Styled as a menu in the same language as `MultiSelectPopover` — same radius, border, layered
 * shadow, entrance and row padding — so it reads as one of the app's dropdowns rather than a panel
 * of its own invention. Rows use the shared `Checkbox`, not a native one.
 *
 * Deliberately a popover rather than dragging the `<th>`s in the live table. The table already owns
 * a drag gesture for *rows* (reorder mode), and a second drag on the same element in a different
 * axis is ambiguous to mouse and screen reader users alike. The table is also `table-layout: auto`,
 * so its widths come from cell content and would resize mid-drag. One compact list avoids both.
 *
 * Structural columns (drag handle, checkbox, Name, actions) are absent entirely rather than listed
 * as disabled — each carries a capability, so they are not choices a coach is being denied.
 */

const DragHandleIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="6" cy="4" r="1.15" />
    <circle cx="10" cy="4" r="1.15" />
    <circle cx="6" cy="8" r="1.15" />
    <circle cx="10" cy="8" r="1.15" />
    <circle cx="6" cy="12" r="1.15" />
    <circle cx="10" cy="12" r="1.15" />
  </svg>
);

const SortableColumnRow = ({
  column,
  isVisible,
  isFirst,
  onToggle,
}: {
  column: PlayersColumn;
  isVisible: boolean;
  /**
   * Carries the `players.columns.reorder` tour anchor. One row only: the overlay spotlights the
   * first visible match, and tagging every handle would leave the list with as many copies of one
   * anchor as it has columns.
   */
  isFirst: boolean;
  onToggle: (key: string, visible: boolean) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: column.key });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`column-settings__row${
        isDragging ? " column-settings__row--dragging" : ""
      }${isVisible ? "" : " column-settings__row--hidden"}`}
    >
      {/*
        The handle owns the drag, not the whole row — otherwise the checkbox could not be clicked
        without starting a drag.
      */}
      <button
        type="button"
        className="column-settings__handle"
        aria-label={`Reorder ${column.label} column`}
        {...(isFirst ? tourAnchor("players.columns.reorder") : {})}
        {...attributes}
        {...listeners}
      >
        <DragHandleIcon />
      </button>
      <Checkbox
        className="column-settings__toggle"
        checked={isVisible}
        onChange={(e) => onToggle(column.key, e.target.checked)}
        label={
          <span className="column-settings__label-text">{column.label}</span>
        }
      />
    </li>
  );
};

export interface ColumnSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The control that opens this panel. A mousedown on it is not an outside click: without this the
   * panel closes on the press and the trigger's own click re-opens it, so the button looks like it
   * does nothing and nothing can close the panel from the outside — including the guided tour step
   * that asks a coach to close it. `TeamChips` and `TeamFilter` hold the same rule by wrapping
   * trigger and menu in one ref.
   */
  triggerRef?: RefObject<HTMLElement | null>;
  tagDefinitions: DefaultTagDefinition[];
  layout: PlayersColumnLayout;
  onLayoutChange: (layout: PlayersColumnLayout) => void;
}

export const ColumnSettingsPanel = ({
  isOpen,
  onClose,
  triggerRef,
  tagDefinitions,
  layout,
  onLayoutChange,
}: ColumnSettingsPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Without this the list is mouse-only (WCAG 2.1.1). Matches TeamChips.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef?.current?.contains(target)) return;
      if (panelRef.current && !panelRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  /*
    Listed in the order they render, so the panel reflects the table. Both come from
    resolveColumnLayout, the same rules buildPlayersColumns applies: a column missing from the
    saved order is appended (so a newly created tag category shows up here too), and an appended
    column that is off by default (School) starts unticked.

    Every write below saves the full displayed order. Ticking School therefore puts it in the
    order and out of `hidden`, which is what keeps it on after a reload. Until then it stays in
    `hidden`, so rearranging other columns does not switch it on by accident.
  */
  const { ordered: displayed, hidden } = resolveColumnLayout(
    configurableColumns(tagDefinitions),
    layout
  );
  const isCustomised = layout.order.length > 0 || layout.hidden.length > 0;

  const handleToggle = (key: string, visible: boolean) => {
    const nextHidden = new Set(hidden);
    if (visible) {
      nextHidden.delete(key);
    } else {
      nextHidden.add(key);
    }
    onLayoutChange({
      order: displayed.map((c) => c.key),
      hidden: [...nextHidden],
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = displayed.findIndex((c) => c.key === active.id);
    const newIndex = displayed.findIndex((c) => c.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onLayoutChange({
      order: arrayMove(displayed, oldIndex, newIndex).map((c) => c.key),
      hidden: [...hidden],
    });
  };

  return (
    <div className="column-settings" ref={panelRef}>
      <div className="column-settings__header">
        <span className="column-settings__title" id="column-settings-title">
          Columns
        </span>
        <span className="column-settings__hint">Tick to show, drag to reorder</span>
      </div>

      {/*
        Reset sits at the top and only when there is something to reset, matching the "Clear all"
        row in MultiSelectPopover. A permanent footer link would advertise an action that does
        nothing on a default layout.
      */}
      {isCustomised && (
        <button
          type="button"
          className="column-settings__reset"
          onClick={() => onLayoutChange({ order: [], hidden: [] })}
        >
          Reset to default
        </button>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={displayed.map((c) => c.key)}
          strategy={verticalListSortingStrategy}
        >
          {/*
            role="group" + aria-labelledby rather than wrapping the list in a <label>: a label
            naming a set of controls is not a label, and trips label-has-associated-control
            (see web/CLAUDE.md).
          */}
          <ul
            className="column-settings__list"
            role="group"
            aria-labelledby="column-settings-title"
            {...tourAnchor("players.columns.visibility")}
          >
            {displayed.map((column, index) => (
              <SortableColumnRow
                key={column.key}
                column={column}
                isVisible={!hidden.has(column.key)}
                isFirst={index === 0}
                onToggle={handleToggle}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};
